import React from 'react';
import { BiListUl } from 'react-icons/bi';
import { FaMapMarkerAlt, FaLocationArrow } from 'react-icons/fa';
import { HiOutlineClipboardList } from 'react-icons/hi';
import { BsLightningCharge } from 'react-icons/bs';

interface JobPrepSummaryCardsProps {
    summaryData?: Record<string, number>;
}

const JobPrepSummaryCards: React.FC<JobPrepSummaryCardsProps> = ({
    summaryData = { totalPole: 0, poleA: 0, poleB: 0, poleC: 0 },
}) => {
    // Determine icon and color scheme based on the key name or index
    const getCardStyle = (key: string, index: number) => {
        if (key.toLowerCase().includes('total')) {
            return {
                icon: <BiListUl className="text-xl" />,
                bgClass: 'bg-blue-50 dark:bg-blue-900/30',
                textClass: 'text-blue-600 dark:text-blue-400',
                labelClass: 'text-gray-500 dark:text-gray-400',
            };
        }
        // Rotate through some predefined styles for other keys
        const styles = [
            {
                icon: <FaMapMarkerAlt className="text-lg" />,
                bgClass: 'bg-emerald-50 dark:bg-emerald-900/30',
                textClass: 'text-emerald-600 dark:text-emerald-400',
                labelClass: 'text-emerald-600 dark:text-emerald-400',
            },
            {
                icon: <HiOutlineClipboardList className="text-xl" />,
                bgClass: 'bg-amber-50 dark:bg-amber-900/30',
                textClass: 'text-amber-500 dark:text-amber-400',
                labelClass: 'text-gray-500 dark:text-gray-400',
            },
            {
                icon: <FaLocationArrow className="text-base" />,
                bgClass: 'bg-blue-50 dark:bg-blue-900/30',
                textClass: 'text-blue-600 dark:text-blue-400',
                labelClass: 'text-gray-500 dark:text-gray-400',
            },
            {
                icon: <BsLightningCharge className="text-base" />,
                bgClass: 'bg-purple-50 dark:bg-purple-900/30',
                textClass: 'text-purple-600 dark:text-purple-400',
                labelClass: 'text-gray-500 dark:text-gray-400',
            },
        ];
        return styles[index % styles.length];
    };

    return (
        <div className="mt-5 mb-6">
            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-2">
                Summary
            </h2>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4 shadow-xs">
                {/* Metric Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 flex-1">
                    {Object.entries(summaryData).map(([key, value], index) => {
                        const style = getCardStyle(key, index);
                        const formatKey = (k: string) => {
                            const map: Record<string, string> = {
                                totalPole: 'Total Pole',
                                poleA: 'Pole A',
                                poleB: 'Pole B',
                                poleC: 'Pole C',
                            };
                            return map[k] || k;
                        };
                        return (
                            <div
                                key={key}
                                className="border border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-750/50 rounded-xl p-3 flex items-center gap-3 transition-all hover:shadow-xs"
                            >
                                <div
                                    className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${style.bgClass} ${style.textClass}`}
                                >
                                    {style.icon}
                                </div>
                                <div>
                                    <span
                                        className={`block text-[11px] font-medium ${style.labelClass}`}
                                    >
                                        {formatKey(key)}
                                    </span>
                                    <span className="text-lg font-bold text-gray-800 dark:text-white leading-tight">
                                        {value}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Right Side: Data Section (Hidden for now as requested) */}
            </div>
        </div>
    );
};

export default JobPrepSummaryCards;
